<script lang="ts" setup>
import { TirPmSearchInput } from "tir-pm-search-input";
import { computed, PropType, provide, ref } from "vue";
import { ParametersTemplateModel } from "../models";
import {
  TaskParameterModel,
  TaskParameterTypeEnum,
  SidebarMenu,
  ChooseProjectDropdown,
} from "@/components";
import { TaskParametersTemplatesList } from "../components";
import { projectInjectKey } from "@/inject-keys";
import { ProjectModel } from "@/models";
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmPlate,
  TirPmStatusBudge,
} from "tir-components";
import { ProjectsService } from "../adapter-service";
import { ArrowUturnUpIcon } from "tir-style-system/icons/outline";

/**API сервис */
const apiService = new ProjectsService();

/**Параметры */
const props = defineProps({
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
});
/**События */
const emit = defineEmits<{
  /**Импорт параметров */
  import: [parameters: TaskParameterModel[]];
  /**Выйти из импорта */
  back: [];
}>();

/**Поисковой запрос */
const search = ref("");
/**Состояние отображения сообщения */
const isShowMessage = ref<Boolean>(true);
/**Список шаблонов */
const templates = ref<ParametersTemplateModel[]>([]);

/**Выбранный проект */
const selectedProject = ref<ProjectModel>();
/**Id рабочей области */
const namespaceId = ref<string>();
/**Id папки */
const groupId = ref<number>();
/**Id очереди */
const queueId = ref<string>();
/**Загрузка сущностей */
const isLoading = ref(false);

/**Экспорт выбранного проекта */
provide(projectInjectKey, selectedProject);

/**Текст "Найдено шаблонов: n" */
const searchedTemplatesText = computed(() => `Найдено очередей: ${templates.value.length}`);
/**Подпись к полю ввода */
const searchLabel = computed(() => {
  let param = "";
  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      param = "статусам";
      break;
    case TaskParameterTypeEnum.Priority:
      param = "приоритетам";
      break;
    case TaskParameterTypeEnum.Tag:
      param = "тегам";
      break;
    case TaskParameterTypeEnum.Type:
      param = "типам";
      break;
    case TaskParameterTypeEnum.Difficulty:
      param = "сложностям";
      break;
  }
  return `Поиск по ${param} и очередям`;
});

/**Текст в сообщении */
const messageText = computed(() => {
  let param = "";
  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      param = "статусы";
      break;
    case TaskParameterTypeEnum.Priority:
      param = "приориты";
      break;
    case TaskParameterTypeEnum.Tag:
      param = "теги";
      break;
    case TaskParameterTypeEnum.Type:
      param = "типы";
      break;
    case TaskParameterTypeEnum.Difficulty:
      param = "сложности";
      break;
  }
  return param;
});

/**Отображать только рабочие области */
const isOnlyNamespaces = computed(
  () => props.type == TaskParameterTypeEnum.Difficulty || props.type == TaskParameterTypeEnum.Type,
);
/**Виден ли блок с поиском */
const isSearchVisible = computed(
  () => props.type == TaskParameterTypeEnum.Status || props.type == TaskParameterTypeEnum.Priority,
);
/**Виден ли сайдбар */
const isSidebarVisible = computed(
  () => !!selectedProject.value && props.type != TaskParameterTypeEnum.Tag,
);
/**Видна ли кнопка, для выходя из импорта */
const isBackButtonVisible = computed(
  () =>
    props.type == TaskParameterTypeEnum.Type ||
    props.type == TaskParameterTypeEnum.Difficulty ||
    props.type == TaskParameterTypeEnum.Tag,
);

/**Импортировать параметры очереди */
const importParameters = (template: ParametersTemplateModel) => {
  emit("import", template.ParsedData);
};
/**Выбор проекта */
const selectProject = (project: ProjectModel) => {
  selectedProject.value = project;
  if (props.type == TaskParameterTypeEnum.Tag) {
    getEntityParameters(project.Id);
  } else {
    clearIds();
  }
};
/**Получение параметров рабочей области */
const getNamespaceParameters = async (_namespaceId?: string) => {
  await getEntityParameters(undefined, _namespaceId);
};
/**Получение параметров папки */
const getGroupParameters = async (_namespaceId?: string, _groupId?: number) => {
  await getEntityParameters(undefined, _namespaceId, _groupId);
};
/**Получение параметров очереди */
const getQueueParameters = async (_namespaceId?: string, _queueId?: string) => {
  await getEntityParameters(undefined, _namespaceId, undefined, _queueId);
};
/**Получение параметров сущности */
const getEntityParameters = async (
  _projectId?: number,
  _namespaceId?: string,
  _groupId?: number,
  _queueId?: string,
) => {
  isLoading.value = true;

  if (_namespaceId || _groupId || _queueId) {
    clearIds();
    namespaceId.value = _namespaceId;
    groupId.value = _groupId;
    queueId.value = _queueId;
  }

  const searchRequest = isSearchVisible.value ? search.value : "";
  const response = await apiService.GetEntityParameters(
    props.type,
    searchRequest,
    _projectId,
    namespaceId.value,
    groupId.value,
    queueId.value,
  );

  if (response.IsSuccess) templates.value = response.Value;

  isLoading.value = false;
};
/**Очистка id сущности */
const clearIds = () => {
  namespaceId.value = undefined;
  groupId.value = undefined;
  queueId.value = undefined;
};
/**Изменение состояния отображения сообщения */
const changeIsShow = () => {
  isShowMessage.value = !isShowMessage.value;
};
/**Обновить список сущностей по поиску */
const updateSearch = async () => {
  if (queueId.value) {
    await getQueueParameters();
  } else if (groupId.value) {
    await getGroupParameters();
  } else if (namespaceId) {
    await getNamespaceParameters();
  }
};

/**Выйти из импорта */
const goBack = () => {
  emit("back");
};
</script>
<template>
  <div class="template-import-form">
    <div v-if="isBackButtonVisible" class="template-import-form_back" @click="goBack">
      <ArrowUturnUpIcon />
      Назад
    </div>
    <div v-if="isSearchVisible" class="template-import-form_search">
      <TirPmSearchInput
        v-model="search"
        is-small
        class="template-import-form_search_input"
        placeholder=""
        :isLoading="isLoading"
        :isWithPopup="false"
        :label="searchLabel"
        @update:model-value="updateSearch"
      />
      <div v-text="searchedTemplatesText" class="template-import-form_search_text" />
    </div>
    <TirPmPlate
      :isShow="isShowMessage"
      variant="attention"
      positionControlAddons="bottom"
      is-bordered
      class="template-import-form_plate"
    >
      <template #leftAddon
        ><TirPmStatusBudge statusIconType="alert" statusColor="alert"
      /></template>
      <template #subtitleSlot
        >В текущий список добавится набор {{ messageText=="статусы"?"статусов":"приоритетов" }} из выбранной очереди. Ранее созданные и не
        использованные в тикетах {{ messageText }} из списка удалятся</template
      ><template #controlsAddons>
        <div class="template-import-form_plate-button">
          <TirPmButton
            :variant="TirPmButtonVariantEnum.SecondaryState"
            :size="TirPmButtonSizeEnum.Small"
            @click="changeIsShow"
            >Понятно</TirPmButton
          >
        </div>
      </template>
    </TirPmPlate>
    <TaskParametersTemplatesList
      is-import
      :type="type"
      :templates="templates"
      @select="importParameters"
    />
    <div class="template-import-form_sidebar">
      <ChooseProjectDropdown :model-value="selectedProject" @update:model-value="selectProject" />
      <SidebarMenu
        v-if="isSidebarVisible"
        is-static
        :only-namespaces="isOnlyNamespaces"
        hideProjectInfo
        :width="256"
        @open-namespace="getNamespaceParameters"
        @open-group="getGroupParameters"
        @open-queue="getQueueParameters"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.template-import-form {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 2.25rem;
  &_plate {
    height: 8.25rem;
  }
  &_plate-button {
    display: flex;
    grid-area: 0.5rem;
  }
  &_search {
    display: flex;
    justify-content: space-between;
    &_input {
      width: 22.5rem;
    }
    &_text {
      color: var(--text-primary, #272d37);
      font-size: 0.875rem;
      font-weight: 500;
      max-height: fit-content;
    }
  }
}
</style>
<style lang="scss">
.template-import-form_sidebar {
  .sidebar {
    border: none;
  }
  position: absolute;
  top: -0.75rem;
  right: calc(100% + 1.5rem + 0.75rem);
  width: 17.5rem;
  height: calc(100% + 1.5rem);
  animation: slide 0.5s linear;
  z-index: 1;
  background-color: var(--bg-base);
  border-radius: 0.5rem;
  border: 0.0625rem solid var(--neutral-10, #eaeaeb);
  padding: 0.75rem;
  overflow: hidden;
  @keyframes slide {
    from {
      width: 0;
    }
    to {
      width: 17.5rem;
    }
  }
}
.template-import-form_back {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
  height: 3rem;
  padding: 0 1.25rem;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 500;
  transition: 0.2s linear;
  border-radius: 0.75rem;
  cursor: pointer;
  svg {
    width: 1.5rem;
    height: 1.5rem;
    transform: rotate(-90deg);
  }
  &:hover {
    background: var(--neutral-translucent-5);
  }
}
</style>
